import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Zap, MapPin, Flame, Loader2, TrendingUp, Music, Film, Newspaper,
  Calendar, Radio, Hash, ChevronRight, Users, Play, ArrowRight,
} from 'lucide-react';

const KIND_META = {
  track: { icon: Music, tint: 'from-fuchsia-500/20 to-violet-500/20', label: 'Track' },
  reel: { icon: Film, tint: 'from-orange-500/20 to-rose-500/20', label: 'Video' },
  post: { icon: Newspaper, tint: 'from-cyan-500/20 to-blue-500/20', label: 'Post' },
  event: { icon: Calendar, tint: 'from-emerald-500/20 to-teal-500/20', label: 'Event' },
  story: { icon: Radio, tint: 'from-amber-500/20 to-orange-500/20', label: 'Story' },
};

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const diff = (Date.now() - t) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ''; }
}

function PulseCard({ item, onClick }) {
  const meta = KIND_META[item.kind] || KIND_META.story;
  const Icon = meta.icon;
  return (
    <Card
      onClick={onClick}
      data-testid={`pulse-card-${item.kind}-${item.id}`}
      className="group cursor-pointer overflow-hidden rounded-2xl bg-card/60 border-border hover:border-violet-500/50 transition-all hover:-translate-y-0.5"
    >
      <div className={`relative aspect-[16/10] bg-gradient-to-br ${meta.tint}`}>
        {item.image ? (
          <img src={item.image} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon className="w-12 h-12 text-white/40" />
          </div>
        )}
        <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm text-[10px] font-semibold text-white uppercase tracking-wide">
          <Icon className="w-3 h-3" />
          {item.category_label || meta.label}
        </div>
        {item.kind === 'track' || item.kind === 'reel' ? (
          <div className="absolute bottom-2 right-2 w-10 h-10 rounded-full bg-white/95 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Play className="w-4 h-4 text-black fill-black ml-0.5" />
          </div>
        ) : null}
      </div>
      <div className="p-3 space-y-1">
        <h3 className="font-semibold text-sm line-clamp-2 group-hover:text-violet-400 transition-colors" data-testid={`pulse-title-${item.id}`}>
          {item.title || '(untitled)'}
        </h3>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="truncate">{item.subtitle || '—'}</span>
          {item.region ? (
            <>
              <span className="text-border">·</span>
              <span className="flex items-center gap-0.5 truncate">
                <MapPin className="w-3 h-3" />{item.region}
              </span>
            </>
          ) : null}
          <span className="ml-auto shrink-0">{timeAgo(item.published_at)}</span>
        </div>
      </div>
    </Card>
  );
}

function BreakingRow({ items, onClick }) {
  if (!items?.length) return null;
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <Flame className="w-5 h-5 text-orange-500" />
        <h2 className="text-lg font-bold uppercase tracking-tight">Breaking Now</h2>
        <span className="ml-2 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-orange-500">
          <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
          Last 6 hrs
        </span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scroll-smooth snap-x snap-mandatory">
        {items.map((it) => (
          <button
            key={`brk-${it.id}`}
            onClick={() => onClick(it)}
            data-testid={`breaking-${it.id}`}
            className="shrink-0 w-64 snap-start text-left"
          >
            <Card className="overflow-hidden rounded-xl border-orange-500/30 hover:border-orange-500/70 transition-colors">
              <div className="relative aspect-video bg-muted/30">
                {it.image ? (
                  <img src={it.image} alt={it.title} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-orange-500/30 to-rose-500/30" />
                )}
                <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-orange-500 text-white text-[10px] font-bold uppercase">
                  {it.badge || 'Breaking'}
                </span>
              </div>
              <div className="p-2.5">
                <p className="font-semibold text-sm line-clamp-1">{it.title}</p>
                <p className="text-xs text-muted-foreground line-clamp-1">{it.subtitle}</p>
              </div>
            </Card>
          </button>
        ))}
      </div>
    </div>
  );
}

function CreatorPill({ c, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`trending-creator-${c.user_id}`}
      className="flex items-center gap-3 shrink-0 pr-4 pl-1 py-1 rounded-full bg-card/60 border border-border hover:border-violet-500/50 transition-colors"
    >
      {c.avatar ? (
        <img src={c.avatar} alt={c.name} className="w-9 h-9 rounded-full object-cover" />
      ) : (
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white font-bold text-sm">
          {(c.name || '?')[0]}
        </div>
      )}
      <div className="text-left leading-tight pr-1">
        <p className="text-sm font-semibold truncate max-w-[130px]">{c.name || c.handle}</p>
        <p className="text-[10px] text-muted-foreground flex items-center gap-1">
          <TrendingUp className="w-2.5 h-2.5 text-emerald-500" />
          {c.followers} followers · {c.recent_posts} posts
        </p>
      </div>
    </button>
  );
}

export default function DMVPulsePage() {
  const navigate = useNavigate();
  const [regions, setRegions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [region, setRegion] = useState('dmv');
  const [category, setCategory] = useState(null);
  const [feed, setFeed] = useState([]);
  const [breaking, setBreaking] = useState([]);
  const [creators, setCreators] = useState([]);
  const [hashtags, setHashtags] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load region + category lists once
  useEffect(() => {
    (async () => {
      try {
        const [r, c] = await Promise.all([
          fetch(`${API}/pulse/regions`).then(x => x.json()),
          fetch(`${API}/pulse/categories`).then(x => x.json()),
        ]);
        setRegions(r.regions || []);
        setCategories(c.categories || []);
      } catch (e) { /* noop */ }
    })();
  }, []);

  const loadFeed = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ region, limit: '40' });
      if (category) qs.set('category', category);
      const [feedR, brkR, crR, tagR] = await Promise.all([
        fetch(`${API}/pulse/feed?${qs.toString()}`).then(x => x.json()),
        fetch(`${API}/pulse/breaking?region=${region}`).then(x => x.json()),
        fetch(`${API}/pulse/trending-creators?region=${region}`).then(x => x.json()),
        fetch(`${API}/pulse/trending-hashtags`).then(x => x.json()),
      ]);
      setFeed(feedR.items || []);
      setBreaking(brkR.items || []);
      setCreators(crR.creators || []);
      setHashtags(tagR.hashtags || []);
    } catch (e) {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, [region, category]);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  const activeRegion = useMemo(() => regions.find(r => r.slug === region), [regions, region]);

  const handleItemClick = (item) => {
    if (item.link) navigate(item.link);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-600/20 via-fuchsia-500/10 to-orange-500/10" />
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(139,92,246,0.4), transparent 40%), radial-gradient(circle at 80% 60%, rgba(236,72,153,0.35), transparent 45%)' }} />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-500 flex items-center justify-center shadow-lg">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight" data-testid="pulse-title">DMV Pulse</h1>
              <p className="text-sm text-muted-foreground">Real-time discovery hub for {activeRegion?.label || 'the DMV'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Region + Category filters */}
        <div className="flex flex-col gap-3 mb-8" data-testid="pulse-filters">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-4 px-4">
            {regions.map((r) => (
              <button
                key={r.slug}
                onClick={() => setRegion(r.slug)}
                data-testid={`pulse-region-${r.slug}`}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
                  region === r.slug
                    ? 'bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white border-transparent shadow-md'
                    : 'bg-card/60 border-border hover:border-violet-500/40 text-muted-foreground hover:text-foreground'
                }`}
              >
                <MapPin className="w-3 h-3 inline mr-1 -mt-0.5" />
                {r.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-4 px-4">
            <button
              onClick={() => setCategory(null)}
              data-testid="pulse-cat-all"
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide transition-all border ${
                !category ? 'bg-foreground text-background border-transparent' : 'border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                onClick={() => setCategory(category === c.slug ? null : c.slug)}
                data-testid={`pulse-cat-${c.slug}`}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide transition-all border ${
                  category === c.slug ? 'bg-foreground text-background border-transparent' : 'border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Breaking now row */}
        <BreakingRow items={breaking} onClick={handleItemClick} />

        {/* Trending creators */}
        {creators.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-5 h-5 text-violet-500" />
              <h2 className="text-lg font-bold uppercase tracking-tight">Trending Creators</h2>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
              {creators.map((c) => (
                <CreatorPill
                  key={c.user_id}
                  c={c}
                  onClick={() => navigate(c.handle ? `/@${c.handle}` : `/artist/${c.user_id}`)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Trending hashtags */}
        {hashtags.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <Hash className="w-5 h-5 text-fuchsia-500" />
              <h2 className="text-lg font-bold uppercase tracking-tight">Trending Hashtags</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {hashtags.map((h) => (
                <button
                  key={h.tag}
                  onClick={() => navigate(`/search?q=%23${h.tag}`)}
                  data-testid={`pulse-hashtag-${h.tag}`}
                  className="px-3 py-1.5 rounded-full bg-fuchsia-500/10 border border-fuchsia-500/30 text-sm text-fuchsia-300 hover:bg-fuchsia-500/20 hover:border-fuchsia-500/60 transition-colors"
                >
                  #{h.tag}
                  <span className="ml-1.5 text-[10px] text-muted-foreground">{h.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Feed grid */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              <h2 className="text-lg font-bold uppercase tracking-tight">Pulse Feed</h2>
              {category && (
                <Badge variant="outline" className="ml-2 text-xs">
                  {categories.find(c => c.slug === category)?.label}
                </Badge>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadFeed}
              disabled={loading}
              data-testid="pulse-refresh"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="aspect-[16/13] rounded-2xl" />
              ))}
            </div>
          ) : feed.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground" data-testid="pulse-empty">
              <Zap className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">
                Nothing pulsing here just yet for <span className="font-semibold text-foreground">{activeRegion?.label || 'this region'}</span>
                {category ? <> in <span className="font-semibold text-foreground">{categories.find(c => c.slug === category)?.label}</span></> : null}.
              </p>
              <p className="text-xs mt-1">Try a different region or category — or check back soon as creators drop new content.</p>
              <Button
                variant="link"
                onClick={() => { setCategory(null); setRegion('dmv'); }}
                className="mt-3"
                data-testid="pulse-reset-filters"
              >
                Reset filters <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="pulse-feed-grid">
              {feed.map((it) => (
                <PulseCard
                  key={`${it.kind}-${it.id}`}
                  item={it}
                  onClick={() => handleItemClick(it)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer note */}
        <div className="mt-12 text-center text-xs text-muted-foreground">
          <p>DMV Pulse aggregates live activity from across the platform.
            External news + AI summaries coming soon.</p>
        </div>
      </div>
    </div>
  );
}
